import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useOtherCarAssignmentApi } from "../../http/otherCarAssignment/api";
import { UpsertOtherCarAssignment } from "../../http/otherCarAssignment/types";
import ModalDialog from "../ui/ModalDialog";
import OtherCarAssignmentForm, { OtherCarAssignmentFormBody } from "./OtherCarAssignmentForm";

type EditOtherCarAssignmentDialogProps = {
  otherCarAssignmentId: string;
  onClose: (success?: boolean) => void;
  carId?: string;
};

const EditOtherCarAssignmentDialog: FC<EditOtherCarAssignmentDialogProps> = ({
  otherCarAssignmentId,
  onClose,
  carId,
}: EditOtherCarAssignmentDialogProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const userSettings = useUserSettings();
  const aborter = useRef(new AbortController());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const otherCarAssignmentApi = useOtherCarAssignmentApi();

  const otherCarAssignmentSwr = otherCarAssignmentApi.getAdminOtherCarAssignment(
    otherCarAssignmentId,
    aborter.current.signal,
  );
  const [otherCarAssignment, isInitialized] = useDataInitializer(
    otherCarAssignmentSwr.data,
    otherCarAssignmentSwr.error,
  );

  useEffect(() => {
    otherCarAssignmentId && otherCarAssignmentSwr.trigger();
  }, [otherCarAssignmentId]);

  const onSubmit = async (formData: OtherCarAssignmentFormBody): Promise<void> => {
    if (otherCarAssignment?.id) {
      const body: UpsertOtherCarAssignment = {
        carId: carId ?? formData.car.id,
        startDate: formData.startDate,
        endDate: formData.endDate,
        costCenter: formData.costCenter,
        type: formData.type.value,
        currentVersionNumber: otherCarAssignment.versionNumber,
      };

      setIsSubmitting(true);
      try {
        await otherCarAssignmentApi.updateOtherCarAssignment(otherCarAssignment.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose(true);
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.otherCarAssignments.edit}>
      <OtherCarAssignmentForm
        onSubmit={onSubmit}
        isDisabled={isSubmitting || !otherCarAssignment?.id || !userSettings.permissions.otherCarAssignment.update}
        isLoading={!isInitialized}
        otherCarAssignment={otherCarAssignment}
        carId={carId}
      />
    </ModalDialog>
  );
};

export default EditOtherCarAssignmentDialog;
